import { Injectable } from '@angular/core';
import { Constants } from 'src/@smartmanufacturing/common/constants';
import { LanguageEnum } from 'src/@smartmanufacturing/common/enum';
import { LoggedUser } from '../models/session-users/session-users';

@Injectable({
    providedIn: 'root',
})
export class BaseApi {
    constructor() { }

    public getHeaders(): any {
        const grpCode = this.getGroupCodeFromStorage();
        const plantCode = this.getPlantCodeFromStorage();
        const userCode = this.getUserSession()?.userCode;
        const token = this.getTokenFromStorage();
        return {
            Authorization: `Bearer ${token}`,
            'Accept-Language': LanguageEnum.Arabic,
            'plant-code': plantCode ? plantCode : '',
            'group-code': grpCode ? grpCode : '',
            'user-code': userCode ? userCode : ''
        };
    }

    public encodeURL(url: string): string {
        return url.replace(' ', '%20');
    }

    public getTokenFromStorage(): string {
        return localStorage.getItem(Constants.AUTH_TOKEN) as string;
    }

    public removeTokenFromStorage() {
        localStorage.removeItem(Constants.AUTH_TOKEN);
    }
    public saveLanguage(language: string): void {
        localStorage.setItem(Constants.Language_Name, language);
    }

    public getGroupCodeFromStorage(): string {
        return localStorage.getItem(Constants.CURRENT_GROUP_CODE) as string;
    }
    public getLanguage(): string {
        return localStorage.getItem(Constants.Language_Name);
    }

    public removeGroupCodeFromStorage() {
        localStorage.removeItem(Constants.CURRENT_GROUP_CODE);
    }

    public saveGroupCodeSession(groupCode: string): void {
        localStorage.setItem(Constants.CURRENT_GROUP_CODE, groupCode);
    }
    public getPlantCodeFromStorage(): string {
        return localStorage.getItem(Constants.CURRENT_PLANT_CODE) as string;
    }
    public removePlantCodeFromStorage() {
        localStorage.removeItem(Constants.CURRENT_PLANT_CODE);
    }

    public savePlantCodeSession(plantCode: string): void {
        localStorage.setItem(Constants.CURRENT_PLANT_CODE, plantCode);
    }

    public getPlantFromStorage(): any {
        const comp = localStorage.getItem(Constants.CURRENT_PLANT);
        if (comp) {
            return JSON.parse(comp);
        }
        return null;
    }
    public removePlantFromStorage() {
        localStorage.removeItem(Constants.CURRENT_PLANT);
    }

    public savePlantSession(company: any): void {
        localStorage.setItem(Constants.CURRENT_PLANT, JSON.stringify(company));
    }

    public getGroupFromStorage(): any {
        const org = localStorage.getItem(Constants.CURRENT_GROUP);
        if (org) {
            return JSON.parse(org);
        }
        return null;
    }
    public removeGroupFromStorage() {
        localStorage.removeItem(Constants.CURRENT_GROUP);
    }

    public saveGroupSession(org: any): void {
        localStorage.setItem(Constants.CURRENT_GROUP, JSON.stringify(org));
    }

    public saveUserSession(user: LoggedUser): void {
        localStorage.setItem(Constants.LOGGED_IN_USER, JSON.stringify(user));
    }
    public removeUserSession() {
        localStorage.removeItem(Constants.LOGGED_IN_USER);
    }

    public getUserSession(): LoggedUser {
        const user = localStorage.getItem(Constants.LOGGED_IN_USER);
        if (user) {
            return JSON.parse(user);
        }
        return null as any;
    }

    public saveUserMenusSession(userMenus: any): void {
        localStorage.setItem(Constants.USER_MENUS, JSON.stringify(userMenus));
    }
    public removeUserMenusSession() {
        localStorage.removeItem(Constants.USER_MENUS);
    }

    public getUserMenusSession(): any[] {
        const menus = localStorage.getItem(Constants.USER_MENUS);
        if (menus) {
            return JSON.parse(menus);
        }
        return [];
    }
    public saveUserPermissionSession(userPermissions: any): void {
        localStorage.setItem(
            Constants.USER_PERMISSIONS,
            JSON.stringify(userPermissions)
        );
    }

    public getUserPermissionsSession(): any[] {
        const permissions = localStorage.getItem(Constants.USER_PERMISSIONS);
        if (permissions) {
            return JSON.parse(permissions);
        }
        return [];
    }
    public removeUserPermissionsSession() {
        localStorage.removeItem(Constants.USER_PERMISSIONS);
    }

    // parent menu sessions settings

    public saveCurrentParentMenuSession(parentMenu: any): void {
        localStorage.setItem(
            Constants.PARENT_MENU,
            JSON.stringify(parentMenu)
        );
    }

    public getCurrentParentMenuSession(): any {
        const menu = localStorage.getItem(Constants.PARENT_MENU);
        if (menu) {
            return JSON.parse(menu);
        }
        return null;
    }
    public removeCurrentParentMenuSession() {
        localStorage.removeItem(Constants.PARENT_MENU);
    }

    public getCurrentMenuID(): string {
        return localStorage.getItem(Constants.CURRENT_MENU_ID) as string;
    }
    public removeCurrentMenuID() {
        localStorage.removeItem(Constants.CURRENT_MENU_ID);
    }

    public saveCurrentMenuID(menuId: string): void {
        localStorage.setItem(Constants.CURRENT_MENU_ID, menuId);
    }

    // menu cards session storage
    public saveMenuCardsSession(cards: any): void {
        localStorage.setItem(
            Constants.MENU_CARDS,
            JSON.stringify(cards)
        );
    }

    public getMenuCardsSession(): any[] {
        const cards = localStorage.getItem(Constants.MENU_CARDS);
        if (cards) {
            return JSON.parse(cards);
        }
        return [];
    }
    public removeCardsSession() {
        localStorage.removeItem(Constants.MENU_CARDS);
    }
    public getAppURL(applicationVariant: string): any {
        let url = '';
        if (applicationVariant === Constants.CORE_APP_NAME) {
            url = Constants.CORE_APPLICATION_URL;
        } else if (applicationVariant === Constants.UTILITY_APP_NAME) {
            url = Constants.UTILITY_APPLICATION_URL;
        } else if (applicationVariant === Constants.UI_FORM_BUILDER_APP_NAME) {
            url = Constants.UI_BUILDER_APPLICATION_URL;
        }
        return url;
    }

    // Method to set user configurations in localStorage
    public setUserConfigurations(configuration: any): void {
        this.removeUserConfigurations();
        localStorage.setItem(Constants.USER_CONFIGURATIONS, JSON.stringify(configuration));
    }

    public setUserChatBotConfigurations(chatbotConfiguration: any): void {
        this.removeUserChatBotConfigurations();
        localStorage.setItem(Constants.USER_CHATBOT_CONFIGURATIONS, JSON.stringify(chatbotConfiguration));
    }

    public removeUserChatBotConfigurations() {
        localStorage.removeItem(Constants.USER_CHATBOT_CONFIGURATIONS);
    }

    public getUserChatBotConfigurations(): any {
        const data = localStorage.getItem(Constants.USER_CHATBOT_CONFIGURATIONS);
        return data ? JSON.parse(data) : null;
    }

    public removeUserConfigurations() {
        localStorage.removeItem(Constants.USER_CONFIGURATIONS);
    }

    public getUserConfigurations(): any {
        const data = localStorage.getItem(Constants.USER_CONFIGURATIONS);
        return data ? JSON.parse(data) : null;
    }

    public getPlantConfigurations(): any {
        const data = localStorage.getItem(Constants.CURRENT_PLANT);
        return data ? JSON.parse(data) : null;
    }
}
